import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import FullScreenImageModal from './FullScreenImageModal';
import { getImageUrl } from '../config/runtime';

const ImageGallery = ({ 
  project, 
  images, // Template images to mix in with API-fetched images
  title = 'Images',
  backPath,
  backLabel = 'Back to project',
  apiFunction, // Function to fetch images (exploreAPI.getImages or projectAPI.getImages)
  projectId,
  autoLoadAll = false // When true, auto-fetches all pages (useful for explore path)
}) => {
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('latest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Infinite scroll ref
  const loadMoreRef = useRef(null);

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  // Use React Query for infinite scroll
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingInfinite,
    error: infiniteError,
    refetch
  } = useInfiniteQuery({
    queryKey: ['gallery-images', projectId, sortOrder],
    queryFn: ({ pageParam = null }) => {
      if (!apiFunction || !projectId) {
        return Promise.resolve({ data: { data: { images: [], pagination: { hasMore: false } } } });
      }
      return apiFunction(projectId, {
        limit: 20,
        cursor: pageParam,
        sort: sortOrder
      });
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.data?.pagination;
      return pagination?.hasMore ? pagination?.nextCursor : undefined;
    },
    enabled: !!apiFunction && !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  // Auto-load all pages when requested
  useEffect(() => {
    if (!autoLoadAll) return;
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [autoLoadAll, hasNextPage, isFetchingNextPage, fetchNextPage, infiniteData?.pages?.length]);

  // Flatten all pages into a single array
  const enhancedImages = useMemo(() => {
    if (!infiniteData) return [];
    return infiniteData.pages.flatMap(page => page?.data?.data?.images || []);
  }, [infiniteData]);

  // Mix API-fetched images with template images, then apply client-side sort
  const displayImages = useMemo(() => {
    const list = [...enhancedImages, ...(images || [])];
    if (!Array.isArray(list) || list.length === 0) return [];

    // Normalize into objects with takenAt and url preserved
    const normalized = list.map((img) => {
      if (typeof img === 'string') return { url: img, takenAt: null, _raw: img };
      return { ...img, _raw: img };
    });

    // Dedupe by URL
    const seen = new Set();
    const deduped = [];
    for (const n of normalized) {
      const key = n.url || JSON.stringify(n._raw);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(n);
    }

    // Sort by takenAt (fallback to nulls at end for latest, start for oldest)
    const sorted = [...deduped].sort((a, b) => {
      const aTime = a.takenAt ? new Date(a.takenAt).getTime() : null;
      const bTime = b.takenAt ? new Date(b.takenAt).getTime() : null;

      if (sortOrder === 'latest') {
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1; // nulls last
        if (bTime === null) return -1;
        return bTime - aTime;
      } else {
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return -1; // nulls first
        if (bTime === null) return 1;
        return aTime - bTime;
      }
    });

    // Return in original shapes (string or object) to minimize downstream changes
    return sorted.map((n) => n._raw);
  }, [enhancedImages, images, sortOrder]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle sort change
  const handleSortChange = useCallback((newSort) => {
    setSortOrder(newSort);
    setShowSortDropdown(false);
    refetch();
  }, [refetch]);

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImageIndex(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  // Get photographer display name
  const getPhotographerName = (takenBy) => {
    if (!takenBy) return 'Unknown';
    if (takenBy.firstName && takenBy.lastName) {
      return `${takenBy.firstName} ${takenBy.lastName}`;
    }
    if (takenBy.firstName) return takenBy.firstName;
    if (takenBy.username) return `@${takenBy.username}`;
    return 'Unknown';
  };

  const isLoading = isLoadingInfinite;
  const error = infiniteError;

  if (isLoading && displayImages.length === 0) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              {backLabel}
            </button>
          </div>

          {/* Title */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>
          </div>

          {/* Loading state */}
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-300 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading images...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              {backLabel}
            </button>
          </div>

          {/* Error state */}
          <div className="text-center py-12">
            <div className="text-red-500 dark:text-red-400">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-lg font-medium">Error loading images</p>
              <p className="text-sm mt-1">Please try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!displayImages || displayImages.length === 0) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              {backLabel}
            </button>
          </div>

          {/* Title */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>
          </div>

          {/* Divider */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-8"></div>

          {/* No images message */}
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-lg font-medium">No images available</p>
              <p className="text-sm mt-1">This project doesn't have any images to display.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto lg:px-8 py-6">
        <div className="px-4 lg:px-0">
          {/* Back button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              {backLabel}
            </button>
          </div>

          {/* Title and Sort Controls */}
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>

            {/* Sort Dropdown */}
            <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <span>Sort: {sortOrder === 'latest' ? 'Latest' : 'Oldest'}</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>

                {showSortDropdown && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowSortDropdown(false)}
                    ></div>
                    
                    {/* Dropdown menu */}
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                      <button
                        onClick={() => handleSortChange('latest')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-t-lg ${
                          sortOrder === 'latest' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Latest
                      </button>
                      <button
                        onClick={() => handleSortChange('oldest')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors rounded-b-lg ${
                          sortOrder === 'oldest' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Oldest
                      </button>
                    </div>
                  </>
                )}
              </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8 hidden md:block"></div>

        {/* Image Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 px-4 lg:px-0">
          {displayImages.map((image, index) => {
            const imageUrl = typeof image === 'string' ? image : image.url;
            const metadata = typeof image === 'object' ? image : null;
            
            return (
              <div key={`${metadata?.projectId || 'img'}-${index}`} className="group relative">
                <div className="aspect-square relative">
                  <button
                    onClick={() => handleImageClick(index)}
                    className="absolute inset-0 w-full h-full rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800 hover:shadow-lg transition-all duration-200 group-hover:scale-[1.02] p-0 border-0"
                  >
                    <img
                      src={getImageUrl(imageUrl)}
                      alt={`${index + 1} of ${displayImages.length}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                        e.target.style.border = '1px solid #d1d5db';
                      }}
                    />
                    
                    
                  </button>
                </div>

                {/* Metadata overlay */}
                {metadata && (
                  <div className="mt-2 space-y-1">
                    {/* Date */}
                    {metadata.takenAt && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(metadata.takenAt)}
                      </div>
                    )}
                    
                    {/* Photographer */}
                    {metadata.takenBy && (
                      <div className="flex items-center gap-2">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-5 h-5 rounded-full overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                            {metadata.takenBy.avatar ? (
                              <img 
                                src={getImageUrl(metadata.takenBy.avatar)} 
                                alt={metadata.takenBy.username} 
                                className="w-5 h-5 object-cover" 
                              />
                            ) : (
                              <span className="text-[8px] text-white font-bold">
                                {metadata.takenBy.initials || 'U'}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Name */}
                        <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                          {getPhotographerName(metadata.takenBy)}
                        </div>
                      </div>
                    )}
                    
                    {/* Project link (only for clone images) */}
                    {metadata.source === 'clone' && metadata.projectId && metadata.projectName && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        from "{metadata.projectName}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Load more trigger for infinite scroll */}
        {hasNextPage && (
          <div ref={loadMoreRef} className="mt-8 flex justify-center">
            {isFetchingNextPage ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                <span>Loading more images...</span>
              </div>
            ) : (
              <div className="text-gray-400 dark:text-gray-500">
                Scroll to load more
              </div>
            )}
          </div>
        )}

        {/* Images count */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400 px-4 lg:px-0">
          {displayImages.length} image{displayImages.length !== 1 ? 's' : ''}
          {hasNextPage && ' (more available)'}
        </div>
      </div>

      {/* Full Screen Image Modal */}
      <FullScreenImageModal
        images={displayImages.map(img => {
          const imageUrl = typeof img === 'string' ? img : img.url;
          return getImageUrl(imageUrl);
        })}
        initialIndex={selectedImageIndex || 0}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ImageGallery;
